import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { SectionHead, FadeIn, CtaPrimary } from "./Shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = ["Automotive Portraits", "Vehicle Rollers", "Event Cinematics", "Brand Intro Video", "Something Else"];

const EMPTY = { name: "", email: "", phone: "", service: "", location: "", preferred_date: "", message: "" };

export default function Booking() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.service) {
      toast.error("Name, email and service are required.");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/bookings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Submission failed");
      }
      setDone(true);
      setForm(EMPTY);
      toast.success("Booking request received — Diego will get back to you shortly.", { testId: "booking-success-toast" });
      setTimeout(() => setDone(false), 6000);
    } catch (err) {
      toast.error(err.message || "Something went wrong — try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="booking" className="relative py-16 md:py-24 border-t border-white/10" data-testid="booking-section">
      <div className="max-w-[1440px] mx-auto px-5 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <SectionHead index="06" kicker="Booking" title="Let's" accent="Shoot" />
            <FadeIn>
              <p className="text-sm md:text-base text-zinc-400 leading-relaxed max-w-sm">
                Tell me about the car, the event or the brand. Every enquiry lands straight in the production queue
                and gets a personal reply.
              </p>
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold mt-8">
                Greater Toronto Area — Available for travel
              </p>
            </FadeIn>
          </div>

          <div className="lg:col-span-8">
            {done ? (
              <FadeIn>
                <div className="border border-gold/40 bg-gold/5 p-10 md:p-16 text-center" data-testid="booking-success-panel">
                  <p className="font-display text-3xl md:text-4xl font-bold uppercase tracking-tight text-gold">Rolling.</p>
                  <p className="text-sm text-zinc-300 mt-4">Your request is in the queue. Expect a reply from Diego shortly.</p>
                  <button
                    data-testid="booking-reset-button"
                    onClick={() => setDone(false)}
                    className="mt-8 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-400 hover:text-gold transition-colors"
                  >
                    Send another request
                  </button>
                </div>
              </FadeIn>
            ) : (
              <FadeIn delay={0.1}>
                <form onSubmit={submit} className="bg-surface/50 border border-white/10 p-4 sm:p-6 md:p-10" data-testid="booking-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="field-label" htmlFor="bk-name">Full Name *</label>
                      <input id="bk-name" data-testid="booking-input-name" className="field-input" value={form.name} onChange={set("name")} placeholder="Your name" />
                    </div>
                    <div>
                      <label className="field-label" htmlFor="bk-email">Email *</label>
                      <input id="bk-email" data-testid="booking-input-email" type="email" className="field-input" value={form.email} onChange={set("email")} placeholder="you@email.com" />
                    </div>
                    <div>
                      <label className="field-label" htmlFor="bk-phone">Phone</label>
                      <input id="bk-phone" data-testid="booking-input-phone" className="field-input" value={form.phone} onChange={set("phone")} placeholder="+1 (___) ___-____" />
                    </div>
                    <div>
                      <label className="field-label" htmlFor="bk-service">Service *</label>
                      <select id="bk-service" data-testid="booking-input-service" className="field-input appearance-none cursor-pointer" value={form.service} onChange={set("service")}>
                        <option value="">Select a service…</option>
                        {SERVICES.map((s) => <option key={s} value={s} className="bg-surface">{s}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="field-label" htmlFor="bk-location">Shoot Location</label>
                      <input id="bk-location" data-testid="booking-input-location" className="field-input" value={form.location} onChange={set("location")} placeholder="e.g. Downtown Toronto" />
                    </div>
                    <div>
                      <label className="field-label" htmlFor="bk-date">Target Date</label>
                      <input id="bk-date" data-testid="booking-input-date" type="date" className="field-input" value={form.preferred_date} onChange={set("preferred_date")} />
                    </div>
                    <div className="md:col-span-2">
                      <label className="field-label" htmlFor="bk-message">Project Details</label>
                      <textarea id="bk-message" data-testid="booking-input-message" rows={4} className="field-input resize-none" value={form.message} onChange={set("message")} placeholder="The car, the event, the vision…" />
                    </div>
                  </div>
                  <CtaPrimary
                    type="submit"
                    testid="booking-submit-button"
                    disabled={sending}
                    className="mt-8 w-full md:w-auto px-10 py-4"
                  >
                    {sending ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
                    {sending ? "Sending…" : "Send Booking Request"}
                  </CtaPrimary>
                </form>
              </FadeIn>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
